/**
 * localizeError — текст отказа сервера на языке кабинета.
 *
 * ЧТО ИЗМЕНИЛОСЬ В WEB-1874 (этап 2). Раньше этот модуль работал В ОДНУ
 * СТОРОНУ: 174 правила «английский текст сервера → РУССКАЯ фраза» плюс
 * безусловный русский запасной текст (PM 2026-07-14: «всё, включая ошибки,
 * только на русском»). После перевода кабинета человек с английским
 * интерфейсом получал бы английские экраны и русские тосты — механизм
 * работал бы против самой цели карточки (риск №2 разведки).
 *
 * Теперь пары фраз живут в словаре `i18n/dictionaries/errors.ts`
 * (`ERROR_CODE_KEYS` — по машинному коду, `ERROR_PATTERNS` — по тексту), а
 * здесь остаётся только РАЗБОР: что за отказ пришёл и какой ключ ему
 * соответствует. Сигнатура функции и все 100+ мест вызова не тронуты.
 *
 * Правило по языкам:
 *  - `ru` — поведение прежнее, байт-в-байт (те же фразы, тот же порядок
 *    правил, тот же запасной текст);
 *  - `en` — код и регулярка дают английскую половину той же записи; русский
 *    текст, пришедший от сервера, пока показывается как есть (сервер учится
 *    отвечать кодами в этапе 4); незнакомый АНГЛИЙСКИЙ текст показывается
 *    сам, если он похож на человеческую фразу, — англоязычному человеку он
 *    полезнее, чем общее «Something went wrong».
 *
 * ⚠ Запасной текст, переданный вызывающим вторым аргументом, отдаётся как
 * есть: это ЕГО строка, и переводится она вместе с его разделом. Умолчание
 * (без второго аргумента) — из словаря, на языке кабинета.
 */
import {
  ERROR_CODE_KEYS,
  ERROR_PATTERNS,
  errorPhraseByCode,
  errorsStrings,
  isServerErrorCode,
  type ErrorStringKey,
} from '@/i18n/dictionaries/errors';
import { friendlyServerErrorMessage } from '@/api/serverErrorMessages';
import { commonStrings } from '@/i18n/dictionaries/common';
import { pickStrings } from '@/i18n/define';
import { getUiLocale, type UiLocale } from '@/i18n/locale';

/**
 * Совместимость: сторож `localizeError.funnelContent.web1228.test.ts` читает
 * список правил и проверяет, что ни у одного нет липких флагов `/g` и `/y` —
 * с ними `exec` двигал бы `lastIndex` между вызовами и перевод пропадал бы
 * через раз. Правила переехали в словарь, сторож продолжает читать их здесь.
 */
export const ERROR_MAP = ERROR_PATTERNS;

/**
 * Похоже ли на человеческую фразу, которую не стыдно показать англоязычному
 * человеку. Отсекаем машинное: тело JSON, стек, машинный код в UPPER_SNAKE,
 * слишком длинное полотно. Осторожность здесь односторонняя: сомневаемся —
 * показываем запасной текст, а не сырьё.
 */
/**
 * WEB-1959: СОБСТВЕННЫЕ СТРОКИ ТРАНСПОРТА — не причина отказа, а
 * обстоятельства обрыва. Их пишет axios, когда сервер не ответил или ответил
 * без тела, и `api/errorStatus.ts`, когда разбирать нечего вовсе
 * («Unexpected error»). Человеку они не говорят ничего.
 *
 * ⚠ Список НАМЕРЕННО узкий и стоит ПОСЛЕ правил по тексту: «Request failed
 * with status code 500», «Network Error» и «timeout of … exceeded» у реестра
 * уже есть — со своими парами ru/en («Ошибка сервера…», «Ошибка сети…»), и
 * они точнее общего запасного текста. Сюда доходит только то, чего в реестре
 * НЕТ: прочие коды ответа (403, 502) и запасная строка разборщика. В русском
 * кабинете это ничего не меняет — незнакомый английский там не показывается
 * и так (правило PM 2026-07-14); правило нужно английскому кабинету, где
 * такой текст печатался человеку как есть (денежный экран смены тарифа).
 */
const TRANSPORT_ONLY_TEXT =
  /^(?:request failed with status code \d+|network error|timeout(?: of \d+\s?ms)? exceeded|canceled|cancelled|unexpected error)$/i;

function looksLikeHumanSentence(text: string): boolean {
  const trimmed = text.trim();
  if (TRANSPORT_ONLY_TEXT.test(trimmed)) return false;
  if (trimmed.length < 8 || trimmed.length > 200) return false;
  if (/[{}[\]<>]|\bat\s+\w+\s*\(|https?:\/\//i.test(trimmed)) return false;
  if (/^[A-Z0-9_]+$/.test(trimmed)) return false;
  if (!/^[A-Za-z]/.test(trimmed)) return false;
  return /\s/.test(trimmed);
}

/** Русская буква — признак того, что сервер уже ответил по-русски. */
const CYRILLIC = /[а-яё]/i; // i18n-allow: класс символов для РАСПОЗНАВАНИЯ русского ответа сервера, не надпись

/**
 * Ключ фразы для кода из КУРИРУЕМОГО списка `ERROR_CODE_KEYS` — тех
 * нескольких кодов, у которых кабинет говорит ПОДРОБНЕЕ сервера (называет
 * следующий шаг). Они старше реестра и остаются первыми.
 *
 * ⚠ Верхний регистр применяется только к коду, которого НЕТ в реестре.
 * Регистр в реестре значим: `project_frozen` (серверный ключ интегратора) и
 * `PROJECT_FROZEN` (ручка кабинета) — разные отказы с разными текстами, и
 * таких пар шесть. Безусловный `toUpperCase()` слепил бы их в одну.
 */
function curatedPhraseKey(code: string): ErrorStringKey | undefined {
  // Поиск через hasOwnProperty: у обычного объекта в цепочке прототипов есть
  // `toString`, `constructor` и соседи, и `ERROR_CODE_KEYS['toString']` вернул
  // бы ФУНКЦИЮ вместо ключа фразы — отказ с таким `code` показал бы
  // `undefined` на экране (поймано тестом части 2).
  const own = (key: string): ErrorStringKey | undefined =>
    Object.prototype.hasOwnProperty.call(ERROR_CODE_KEYS, key)
      ? ERROR_CODE_KEYS[key]
      : undefined;

  const exact = own(code);
  if (exact) return exact;
  if (isServerErrorCode(code)) return undefined;
  return own(code.toUpperCase());
}

/**
 * СВЕЖИЙ ВЗГЛЯД ПАЧКИ 23: строковое поле отказа с ЛЮБОГО объекта — и с
 * `Error` перехватчика, и с простого объекта. Thunk'и (funnelsSlice,
 * quizzesSlice) отвергают разобранным `{ message, status, code }`, а
 * `extractApiError` возвращает такой же простой объект; раньше лестница
 * читала `code`/`reasonCode` только с `instanceof Error`, и словарная фраза
 * по коду (PIECE_SHARED и соседи) до человека не доходила — печатался общий
 * запасной текст. Читается только строка: число, функция из прототипа и
 * прочее не считаются полем отказа.
 */
function errorField(
  error: unknown,
  field: 'code' | 'reasonCode' | 'message',
): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const value = (error as Record<string, unknown>)[field];
  return typeof value === 'string' ? value : undefined;
}

/**
 * WEB-2461 (слита WEB-2418) — подробности отказа во фразе реестра.
 *
 * У отказа с переменной частью (имя файла, номер страницы, число страниц)
 * фраза реестра держит места `{имя}`, а сервер шлёт значения полем `details`
 * (та же форма, что у `humanError` на сервере). Подставляются только строки и
 * числа и только в места с тем же именем; чужое место (`{click_id}` в
 * подсказке про метки адреса) остаётся как есть. Замена — функцией, поэтому
 * `$&` в имени файла остаётся текстом.
 */
function fillDetails(phrase: string, error: unknown): string {
  if (typeof error !== 'object' || error === null) return phrase;
  const details = (error as { details?: unknown }).details;
  if (
    typeof details !== 'object' ||
    details === null ||
    Array.isArray(details)
  ) {
    return phrase;
  }
  const values = details as Record<string, unknown>;
  return phrase.replace(/\{([A-Za-z]\w*)\}/g, (whole, name: string) => {
    if (!Object.prototype.hasOwnProperty.call(values, name)) return whole;
    const value = values[name];
    return typeof value === 'string' || typeof value === 'number'
      ? String(value)
      : whole;
  });
}

interface PatternHit {
  readonly phrase: string | ((...args: never[]) => string);
  readonly match: RegExpExecArray;
}

/** Первое сработавшее правило по тексту сервера. Порядок правил значим. */
function firstPatternHit(
  text: string,
  locale: UiLocale,
): PatternHit | undefined {
  const s = pickStrings(errorsStrings, locale);
  for (const [pattern, key] of ERROR_PATTERNS) {
    // WEB-1228: exec, а не test — переменная часть отказа (тип блока, экран,
    // потолок списка) нужна переводу-функции. Флага /g ни у одного правила
    // нет, состояние lastIndex между вызовами не копится (сторож выше).
    const match = pattern.exec(text);
    if (match) return { phrase: s[key], match };
  }
  return undefined;
}

/**
 * WEB-3288: фраза причины денежного отказа (`reasonCode`, WEB-1908), если
 * причина есть в реестре кодов; иначе `undefined`.
 */
function knownReasonPhrase(
  error: unknown,
  locale: UiLocale,
): string | undefined {
  const reasonCode = errorField(error, 'reasonCode');
  if (typeof reasonCode !== 'string') return undefined;
  const phrase = errorPhraseByCode(reasonCode, locale);
  return phrase === undefined ? undefined : fillDetails(phrase, error);
}

/**
 * @param error любое значение ошибки (Error, строка, unknown)
 * @param fallback текст для неизвестного сообщения; по умолчанию — из словаря
 */
export function localizeError(error: unknown, fallback?: string): string {
  const locale = getUiLocale();
  const s = pickStrings(errorsStrings, locale);
  const defaultFallback = fallback ?? s.somethingWentWrong;

  // WEB-1253 ревизия: код важнее текста, когда он есть — client.ts кладёт
  // структурный код отдельно от уже-человеческого .message (см. словарь).
  // Проверяем его первым, до текстовых веток.
  const code = errorField(error, 'code');
  const curated = typeof code === 'string' ? curatedPhraseKey(code) : undefined;
  // WEB-3288: у общего кода с кураторской фразой (`INSUFFICIENT_BALANCE` —
  // «недостаточно средств») может прийти ПРИЧИНА из реестра (`reasonCode`,
  // правило WEB-1908) — например, «долг по кредитам» (CREDIT_DEBT_BLOCKED).
  // Причина точнее общей фразы и выигрывает у неё; незнакомая причина —
  // прежний путь по коду.
  if (curated)
    return knownReasonPhrase(error, locale) ?? (s[curated] as string);

  // ВТОРОЙ курируемый список — `api/serverErrorMessages.ts`: там кабинет
  // говорит ПОДРОБНЕЕ сервера (называет причину и следующий шаг), а русская
  // половина под сторожем дословного совпадения с серверными константами.
  // Держать эти коды здесь второй копией нельзя (WEB-1844), поэтому спрашиваем
  // тот же список одной дверью: он старше реестра и бьёт общую формулировку.
  // Перехватчик (client.ts) подменяет им `message` — на путях мимо
  // перехватчика (свои вызовы, старый кэш) фраза берётся отсюда.
  const curatedServer =
    typeof code === 'string'
      ? friendlyServerErrorMessage(code, undefined)
      : undefined;
  if (curatedServer) return knownReasonPhrase(error, locale) ?? curatedServer;

  const text =
    typeof error === 'string' ? error : (errorField(error, 'message') ?? '');
  /**
   * Фраза реестра сервера (этап 4, часть 2) — общая формулировка кода.
   *
   * WEB-1908: у денежного отказа 402 рядом с общим `code` приходит
   * `reasonCode` — ПРИЧИНА («кончились квизы», «домены», «публикация на
   * бесплатном тарифе»). Причина ТОЧНЕЕ общей формулировки, поэтому при
   * прочих равных выигрывает она; общий `code` при этом не трогается и
   * по-прежнему выбирает ОКНО (`utils/tierLimit.ts`, шрам WEB-135), а
   * незнакомая причина (старый фронт против нового сервера) откатывает на
   * прежний путь, а не гасит экран. Место выбрано намеренно: подстановка
   * идёт ИМЕННО в `byCode`, поэтому все правила порядка ниже — правило
   * функции-фразы (WEB-1228/1894) и «в русском кабинете серверный текст с
   * подставленными числами точнее словарного» — остаются в силе слово в
   * слово.
   */
  const reasonCode = errorField(error, 'reasonCode');
  const byReason =
    typeof reasonCode === 'string'
      ? errorPhraseByCode(reasonCode, locale)
      : undefined;
  const byCodeRaw =
    byReason ??
    (typeof code === 'string' ? errorPhraseByCode(code, locale) : undefined);
  const byCode =
    byCodeRaw === undefined ? undefined : fillDetails(byCodeRaw, error);

  if (!text.trim()) return byCode ?? defaultFallback;

  const hit = firstPatternHit(text, locale);

  // ПРАВИЛО ПОРЯДКА (часть 2). Текстовое правило бьёт код ТОЛЬКО когда его
  // фраза — функция: такая фраза достаёт из самого текста переменную часть
  // (тип блока, экран, номер правила логики, потолок списка), которой в
  // реестре по построению нет — код держит ОБЩУЮ формулировку. Отдать здесь
  // приоритет коду значило бы отменить WEB-1228/WEB-1894.
  if (hit && typeof hit.phrase === 'function') {
    return (hit.phrase as (m: RegExpExecArray) => string)(hit.match);
  }

  // Дальше выигрывает КОД: статическое правило по тексту ошибается тем чаще,
  // чем длиннее список (правило `…|500` ловит любую сумму «500 $» и объявляет
  // её ошибкой сервера — поймано тестом части 2, класс WEB-916).
  if (byCode) {
    // В русском кабинете серверный текст ТОЧНЕЕ словарного: сервер
    // подставляет в него числа и имена (сумма предела, имя домена, название
    // организации), а реестр держит общую формулировку. Английскому кабинету
    // этот текст показать нельзя — он русский.
    if (locale === 'ru' && CYRILLIC.test(text)) return text;
    return byCode;
  }

  if (hit) return hit.phrase as string;

  // Уже на русском (кириллица) — доверяем и показываем как есть. Код сюда уже
  // не доходит: он разобран выше.
  if (CYRILLIC.test(text)) return text;

  // Незнакомый английский. В русском кабинете он не показывается никогда
  // (правило PM 2026-07-14 в силе). В английском — показывается сам, если
  // похож на человеческую фразу: это ровно тот текст, который сервер и хотел
  // сказать, и он полезнее общего «Something went wrong».
  if (getUiLocale() === 'en' && looksLikeHumanSentence(text)) {
    return text;
  }

  return defaultFallback;
}

/**
 * WEB-2587 — текст отказа ДВЕРИ УДАЛЕНИЯ.
 *
 * Код `NOT_RESOURCE_OWNER` один на все действия над чужим (удаление, откат,
 * публикация, перенос, копирование), поэтому его фраза в реестре нейтральна к
 * действию. На удалении человеку точнее сказать именно про удаление — эта
 * фраза живёт в `commonStrings.deleteNotOwner` на обоих языках. Любой другой
 * отказ — ровно `localizeError` с тем же запасным текстом вызывающего.
 */
export function localizeDeleteError(error: unknown, fallback?: string): string {
  if (errorField(error, 'code') === 'NOT_RESOURCE_OWNER') {
    const common = pickStrings(commonStrings, getUiLocale());
    return common.deleteNotOwner;
  }
  return localizeError(error, fallback);
}
